import axios from "axios";
import {
  deleteAPI,
  getAPI,
  postAPI,
  postBlobAPI,
  postFileAPI,
  putAPI,
  patchAPI,
  uploadCSVAPI,
} from "../../../api/Networking";
import {
  APIResponse,
  PaginatedAPIResponse,
} from "../../../models/NetworkResponse";
import { GetShipmentsParams } from "../stores/ShipmentStore";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  BulkUpdate,
  BulkUpdateShippingCostResult,
  DeliveryPartner,
  ShipmentsDetails,
  TrackingBulkUpdateJobStatus,
  TrackingBulkUpdateResult,
} from "../../../models/Shipments";
import {
  BulkBookingSyncResult,
  SingleBookingSyncResult,
} from "../../../models/Shipments/ShipmentBooking";
import { SingleTrackingSyncResult } from "../../../models/Shipments/ShipmentTrackingSync";
import {
  ShipmentCreateOptions,
  ShipmentRequest,
} from "../../../models/AddShipment";
import {
  UpdateShipmentAddressRequest,
  UpdateShipmentAddressResult,
  UpdateShipmentDeliveryRequest,
  UpdateShipmentDeliveryResult,
  UpdateShipmentRecipientRequest,
  UpdateShipmentRecipientResult,
} from "../../../models/Shipments/ShipmentDetails";
import {
  AddShipmentBoxRequest,
  AddShipmentBoxResult,
  DeleteShipmentBoxResult,
  ShipmentBox,
} from "../../../models/Shipments/ShipmentBoxes";
import {
  ShipmentActivityLog,
  ShipmentActivityLogFilters,
} from "../../../models/Shipments/ShipmentActivityLog";
import {
  AddShipmentItemLineRequest,
  AddShipmentItemLineResult,
  AddShipmentPackLineRequest,
  AddShipmentPackLineResult,
  BulkAddShipmentItemLineRequest,
  BulkAddShipmentItemLineResult,
  BulkAddShipmentPackLineRequest,
  BulkAddShipmentPackLineResult,
  DispatchShipmentLinesRequest,
  DispatchShipmentLinesResult,
  ListShipmentLinesParams,
  ListShipmentLinesResult,
  RemoveShipmentLineResult,
  RemoveShipmentPackLinesResult,
  ShipmentLineType,
  ShipmentLinesUploadJobStatus,
  ShipmentLinesUploadResult,
} from "../../../models/Shipments/ShipmentInventoryLines";
import dayjs from "dayjs";

const SHIPMENTS_ADMIN_BASE = "/v1/shipments/admin";

export type BulkCancelJob = {
  jobId: number;
  status: string;
  scope: "selected" | "filters";
  totalCount: number;
  processedCount: number;
  cancelledCount: number;
  skippedCount: number;
  failedCount: number;
  errorMessage: string | null;
};

export type CreateBulkCancelResult =
  | { conflict: true; jobId: number }
  | { conflict: false; job: BulkCancelJob };

const mapBulkCancelJob = (raw: any): BulkCancelJob | null => {
  if (!raw) return null;
  const jobId = Number(raw.jobId ?? raw.job_id);
  if (!Number.isFinite(jobId) || jobId <= 0) return null;
  const scope = raw.scope === "filters" ? "filters" : "selected";
  return {
    jobId,
    status: String(raw.status ?? ""),
    scope,
    totalCount: Number(raw.totalCount ?? raw.total_count) || 0,
    processedCount: Number(raw.processedCount ?? raw.processed_count) || 0,
    cancelledCount: Number(raw.cancelledCount ?? raw.cancelled_count) || 0,
    skippedCount: Number(raw.skippedCount ?? raw.skipped_count) || 0,
    failedCount: Number(raw.failedCount ?? raw.failed_count) || 0,
    errorMessage:
      raw.errorMessage == null && raw.error_message == null
        ? null
        : String(raw.errorMessage ?? raw.error_message),
  };
};

const bulkCancelErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
};

export type BulkRemovePacksJob = {
  jobId: number;
  status: string;
  scope: "selected" | "filters";
  totalCount: number;
  processedCount: number;
  removedCount: number;
  skippedCount: number;
  failedCount: number;
  errorMessage: string | null;
};

export type CreateBulkRemovePacksResult =
  | { conflict: true; jobId: number }
  | { conflict: false; job: BulkRemovePacksJob };

const mapBulkRemovePacksJob = (raw: any): BulkRemovePacksJob | null => {
  if (!raw) return null;
  const jobId = Number(raw.jobId ?? raw.job_id);
  if (!Number.isFinite(jobId) || jobId <= 0) return null;
  const scope = raw.scope === "filters" ? "filters" : "selected";
  return {
    jobId,
    status: String(raw.status ?? ""),
    scope,
    totalCount: Number(raw.totalCount ?? raw.total_count) || 0,
    processedCount: Number(raw.processedCount ?? raw.processed_count) || 0,
    removedCount: Number(raw.removedCount ?? raw.removed_count) || 0,
    skippedCount: Number(raw.skippedCount ?? raw.skipped_count) || 0,
    failedCount: Number(raw.failedCount ?? raw.failed_count) || 0,
    errorMessage:
      raw.errorMessage == null && raw.error_message == null
        ? null
        : String(raw.errorMessage ?? raw.error_message),
  };
};

const bulkRemovePacksErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === "string" && message.trim()) return message;
  }
  if (error instanceof Error && error.message.trim()) return error.message;
  return fallback;
};

function unwrapShipmentBoxWriteResult<T extends { boxes: ShipmentBox[] }>(
  response: APIResponse & Partial<T>,
  fallbackMessage: string,
): T {
  const result = (response.result ?? response) as T;
  if (!result?.boxes || !Array.isArray(result.boxes)) {
    throw new Error(response.message || fallbackMessage);
  }
  return result;
}

function unwrapShipmentSectionResult<T extends { shipmentId: number }>(
  response: APIResponse & Partial<T>,
  fallbackMessage: string,
): T {
  try {
    if (response.success === false) {
      throw new Error(response.message || fallbackMessage);
    }

    const result = (response.result ?? response) as T;
    const shipmentId = Number(result?.shipmentId);
    if (!Number.isFinite(shipmentId) || shipmentId <= 0) {
      throw new Error(response.message || fallbackMessage);
    }

    return result;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(fallbackMessage);
  }
}

export default class ShipmentsAPI {
  getQueryParams = (params: any): URLSearchParams => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page_no", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.sortBy) queryParams.append("sort_by", params.sortBy);
    if (params.sortType) queryParams.append("sort_type", params.sortType);
    if (params.search) queryParams.append("search_text", params.search);
    if (params.allSelected === true) queryParams.append("all_selected", "true");
    if (params.tab_id) queryParams.append("tab_id", params.tab_id);

    if (params.status && params.status.length > 0) {
      queryParams.append("status", params.status.join(","));
    }
    if (params.delivery_partner_id && params.delivery_partner_id.length > 0) {
      queryParams.append(
        "delivery_partner_id",
        params.delivery_partner_id.join(","),
      );
    }

    if (params.clientSku && params.clientSku.length > 0) {
      queryParams.append(
        "clientSku",
        params.clientSku
          .slice(0, 20)
          .map((sku: FilterValue) => sku.value || sku.id)
          .filter(Boolean)
          .join(","),
      );
    }

    if (
      params.inventory_type &&
      (params.inventory_type === "1" || params.inventory_type === "2")
    ) {
      queryParams.append("inventory_type", params.inventory_type);
    }

    if (params.shipment_type && params.shipment_type.length > 0) {
      queryParams.append("shipment_type", params.shipment_type.join(","));
    }

    if (params.c_id && params.c_id.length > 0) {
      queryParams.append("c_id", params.c_id[0].id);
    }

    if (params.order_id && params.order_id.length > 0) {
      queryParams.append("order_id", params.order_id.join(","));
    }

    if (params.contact_number && params.contact_number !== "") {
      queryParams.append("contact_number", params.contact_number);
    }

    const DimensionsFilter = [
      { id: 1, value: "dimensions" },
      { id: 2, value: "weight" },
      { id: 3, value: "shipping_cost" },
      { id: 4, value: "awb" },
      { id: 5, value: "packs" },
    ];

    if (params.dimensions && params.dimensions.length > 0) {
      DimensionsFilter.forEach((filter) => {
        if (params.dimensions?.includes(filter.id)) {
          queryParams.append(filter.value, "true");
        }
      });
    }

    if (params.packs_exist !== undefined) {
      queryParams.append("packs_exist", params.packs_exist ? "true" : "false");
    }

    if (params.country && params.country.length > 0) {
      queryParams.append("country", params.country.join(","));
    }

    if (params.search_text_value && params.search_text_value.length > 0) {
      queryParams.append(
        "search_text_value",
        params.search_text_value.join(","),
      );
    }

    if (params.start_date && params.start_date !== "") {
      const start_date = dayjs(params.start_date, "YYYY-MM-DD")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      queryParams.append("start_date", start_date);
    }

    if (params.end_date && params.end_date !== "") {
      const end_date = dayjs(params.end_date, "YYYY-MM-DD")
        .add(1, "day")
        .utc()
        .format("YYYY-MM-DD HH:mm:ss");
      queryParams.append("end_date", end_date);
    }

    return queryParams;
  };

  /**
   * Listing filters for filter-scope bulk cancel.
   * Customer (`c_id`) and/or order (`order_id`) are required by the API; status tab
   * and every other listing filter on screen are also sent so the worker matches
   * what the admin sees.
   */
  bulkCancelFilters = (params: GetShipmentsParams): Record<string, string> => {
    const query = this.getQueryParams(params);
    query.delete("page_no");
    query.delete("limit");
    query.delete("sort_by");
    query.delete("sort_type");
    query.delete("all_selected");
    const filters: Record<string, string> = {};
    query.forEach((value, key) => {
      if (value !== "") filters[key] = value;
    });
    return filters;
  };

  createBulkCancelJob = async (input: {
    reason: string;
    scope: "selected" | "filters";
    shipmentIds: number[];
    filters?: Record<string, string>;
  }): Promise<CreateBulkCancelResult> => {
    try {
      const response = await postAPI(`${SHIPMENTS_ADMIN_BASE}/bulk-cancel`, {
        reason: input.reason,
        scope: input.scope,
        shipment_ids:
          input.scope === "selected" ? input.shipmentIds : undefined,
        filters: input.scope === "filters" ? input.filters : undefined,
      });
      if (response.success === false) {
        throw new Error(response.message || "Couldn't start the bulk cancel.");
      }
      const job = mapBulkCancelJob((response.result as any)?.job);
      if (!job) {
        throw new Error(response.message || "Couldn't start the bulk cancel.");
      }
      return { conflict: false, job };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const jobId = Number(error.response.data?.result?.job_id);
        if (Number.isFinite(jobId) && jobId > 0) {
          return { conflict: true, jobId };
        }
      }
      throw new Error(
        bulkCancelErrorMessage(error, "Couldn't start the bulk cancel."),
      );
    }
  };

  getActiveBulkCancelJob = async (): Promise<BulkCancelJob | null> => {
    try {
      const response = await getAPI(`${SHIPMENTS_ADMIN_BASE}/bulk-cancel/active`);
      if (response.success === false) {
        throw new Error(response.message || "Couldn't check bulk cancel.");
      }
      return mapBulkCancelJob((response.result as any)?.job);
    } catch (error) {
      throw new Error(
        bulkCancelErrorMessage(error, "Couldn't check bulk cancel."),
      );
    }
  };

  getBulkCancelJob = async (jobId: number): Promise<BulkCancelJob> => {
    try {
      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/bulk-cancel/${jobId}`,
      );
      if (response.success === false) {
        throw new Error(response.message || "Couldn't load bulk cancel.");
      }
      const job = mapBulkCancelJob((response.result as any)?.job);
      if (!job) {
        throw new Error(response.message || "Couldn't load bulk cancel.");
      }
      return job;
    } catch (error) {
      throw new Error(
        bulkCancelErrorMessage(error, "Couldn't load bulk cancel."),
      );
    }
  };

  getShipments = async (
    params: GetShipmentsParams,
    download_csv: boolean = false,
    signal?: AbortSignal,
  ): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      if (download_csv) {
        const response = await postBlobAPI(
          `/v5/shipments?${queryParams.toString()}`,
          { download_csv: true, emails: params.emails },
        );
        const blob = new Blob([response], { type: "text/csv" });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `Master_ShipmentReport.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        const response = await postAPI(
          `/v5/shipments?${queryParams.toString()}`,
          { download_csv: false, emails: params.emails },
          signal,
        );
        return response.result as PaginatedAPIResponse<ShipmentsDetails>;
      }
    } catch (error) {
      throw error;
    }
  };

  editShipment = async (
    shipmentId: number,
    shipmentDetails: ShipmentsDetails,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(`/v5/shipments/shipmentId/${shipmentId}`, {
        shipmentDetails: shipmentDetails,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  cancelShipment = async (
    shipmentId: number,
    reason: string,
  ): Promise<
    APIResponse & {
      result?: {
        shipmentId: number;
        status: number;
        reason: string;
        releasedLines: number;
      };
    }
  > => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/cancel`,
        { reason },
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  returnShipment = async (
    shipmentId: number,
    reason?: string,
  ): Promise<
    APIResponse & {
      result?: {
        shipmentId: number;
        status: number;
        reason: string | null;
        reReservedLines: number;
      };
    }
  > => {
    try {
      const trimmedReason = reason?.trim();
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/return`,
        trimmedReason ? { reason: trimmedReason } : {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  removePacksFromShipment = async (
    shipmentId: number,
  ): Promise<APIResponse> => {
    try {
      const response = await putAPI(
        `/v5/shipments/remove/pack/shipmentId/${shipmentId}`,
        {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  moveShipmentToNew = async (shipmentId: number): Promise<APIResponse> => {
    try {
      const response = await patchAPI(
        `/v5/shipments/shipmentId/${shipmentId}/moveToNew`,
        {},
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  listPrinterVendors = async (
    searchText?: string,
  ): Promise<DeliveryPartner[]> => {
    try {
      const queryParams = new URLSearchParams({ limit: "50" });
      const search = searchText?.trim();
      if (search) {
        queryParams.append("search_text", search);
      }

      const response = await postAPI(`/v5/delivery?${queryParams.toString()}`, {
        is_logistics_vendor: false,
        is_printer: true,
      });

      if (response?.success === false) {
        throw new Error(response.message || "Failed to load printers.");
      }

      const result = (response?.result ?? {}) as { list?: DeliveryPartner[] };
      return Array.isArray(result.list) ? result.list : [];
    } catch (error) {
      throw error;
    }
  };

  bulkShipmentUpdate = async (
    payload: BulkUpdate,
    params: GetShipmentsParams,
  ): Promise<APIResponse> => {
    try {
      const {
        shipment_ids,
        length,
        breadth,
        height,
        weight,
        status,
        delivery_partner_id,
        shipment_method,
        booking_type,
        cancelReason,
        declared_value,
      } = payload;

      const queryParams = this.getQueryParams(params);

      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/bulk-update?${queryParams.toString()}`,
        {
          shipmentIds: shipment_ids,
          length,
          breadth,
          height,
          weight,
          status,
          deliveryPartnerId: delivery_partner_id,
          shipmentMethod: shipment_method,
          ...(booking_type !== undefined && { booking_type }),
          ...(cancelReason !== undefined && { cancelReason }),
          ...(declared_value !== undefined && { declared_value }),
        },
      );

      return response;
    } catch (error) {
      throw error;
    }
  };

  uploadCsvForBulkEdit = async (
    file: File | null,
  ): Promise<APIResponse & { result?: TrackingBulkUpdateResult }> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }

      const response = await uploadCSVAPI(
        `${SHIPMENTS_ADMIN_BASE}/tracking/bulk-update`,
        file,
      );
      return response as APIResponse & { result?: TrackingBulkUpdateResult };
    } catch (error) {
      throw error;
    }
  };

  getTrackingBulkUpdateJob = async (
    jobId: number,
  ): Promise<APIResponse & { result?: TrackingBulkUpdateJobStatus }> => {
    try {
      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/tracking/bulk-update/jobs/${jobId}`,
      );
      return response as APIResponse & {
        result?: TrackingBulkUpdateJobStatus;
      };
    } catch (error) {
      throw error;
    }
  };

  bulkUpdateShippingCost = async (
    shipmentIds: number[],
    shippingCost: number,
  ): Promise<APIResponse & { result?: BulkUpdateShippingCostResult }> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipping-cost/bulk-update`,
        { shipmentIds, shipping_cost: shippingCost },
      );
      return response as APIResponse & {
        result?: BulkUpdateShippingCostResult;
      };
    } catch (error) {
      throw error;
    }
  };

  syncBooking = async (): Promise<
    APIResponse & { result?: BulkBookingSyncResult }
  > => {
    try {
      const response = await postAPI(`${SHIPMENTS_ADMIN_BASE}/booking/sync`);
      return response as APIResponse & { result?: BulkBookingSyncResult };
    } catch (error) {
      throw error;
    }
  };

  syncShipmentBooking = async (
    shipmentId: number,
    options?: { requeue?: boolean },
  ): Promise<APIResponse & { result?: SingleBookingSyncResult }> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/booking/sync`,
        options?.requeue ? { requeue: true } : {},
      );
      return response as APIResponse & { result?: SingleBookingSyncResult };
    } catch (error) {
      throw error;
    }
  };

  syncShipmentTracking = async (
    shipmentId: number,
  ): Promise<APIResponse & { result?: SingleTrackingSyncResult }> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/tracking/sync`,
      );
      return response as APIResponse & { result?: SingleTrackingSyncResult };
    } catch (error) {
      throw error;
    }
  };

  getOrder = async (orderId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(`/v5/shipments/order-id/${orderId}`);
      return response;
    } catch (error) {
      throw error;
    }
  };

  downloadShiprocketCSV = async (params: GetShipmentsParams): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      const response = await postBlobAPI(
        `/v5/shipments/shiprocket_template?${queryParams.toString()}`,
        { emails: params.emails },
      );
      const blob = new Blob([response], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      const now = new Date().toLocaleString("en-GB", {
        timeZone: "Asia/Kolkata",
      });
      const formattedDate = now.replace(/\//g, "-").replace(",", "_");
      const fileName = `shiprocket_template_${formattedDate}.csv`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw error;
    }
  };

  downloadBulkShipmentsCSVReport = async (
    params: GetShipmentsParams,
  ): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      const response = await getAPI(
        `/v5/shipments/download/bulk?${queryParams.toString()}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getShiprocketCSVBulkReport = async (
    params: GetShipmentsParams,
  ): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      const response = await getAPI(
        `/v5/shipments/shiprocket_template/bulk?${queryParams.toString()}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  downloadShipmozoCSV = async (params: GetShipmentsParams): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      const blob = await postFileAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipmozo_template?${queryParams.toString()}`,
        { emails: params.emails },
      );
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      const now = new Date().toLocaleString("en-GB", {
        timeZone: "Asia/Kolkata",
      });
      const formattedDate = now.replace(/\//g, "-").replace(",", "_");
      a.download = `shipmozo_template_${formattedDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      throw error;
    }
  };

  getShipmozoCSVBulkReport = async (
    params: GetShipmentsParams,
  ): Promise<any> => {
    try {
      const queryParams = this.getQueryParams(params);

      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipmozo_template/bulk?${queryParams.toString()}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  searchPacks = async (search_text: string): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `/v5/shipments/search-packs?search_text=${search_text}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  createShipment = async (shipment: ShipmentRequest): Promise<APIResponse> => {
    try {
      const response = await postAPI(`/v5/shipments/shipmentRequest`, {
        shipment,
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  getShipmentCreateOptions = async (
    orderId: number,
  ): Promise<APIResponse & { result?: ShipmentCreateOptions }> => {
    try {
      const response = await getAPI(
        `/v5/shipments/shipment-create-options?order_id=${orderId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  createShipmentThroughCsv = async (
    orderId: number,
    file: File | null,
  ): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }
      const response = await uploadCSVAPI(
        `/v5/shipments/csvShipmentRequest/${orderId}`,
        file,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  createBulkShipmentsViaInventoryIdAndCsv = async (
    inventoryId: number,
    file: File | null,
    orderId?: number | null,
  ): Promise<APIResponse> => {
    try {
      if (!file) {
        throw new Error("No file provided for upload.");
      }
      const query = orderId ? `?order_id=${orderId}` : "";
      const response = await uploadCSVAPI(
        `/v5/shipments/csvShipmentRequest/inventory-id/${inventoryId}${query}`,
        file,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getCountByStatus = async (
    params: GetShipmentsParams = {},
  ): Promise<APIResponse> => {
    try {
      // Same filters as the listing; the backend reads emails from the query here.
      const queryParams = this.getQueryParams(params);
      if (params.emails) queryParams.append("emails", params.emails);
      const queryString = queryParams.toString();
      const response = await getAPI(
        queryString
          ? `/v5/shipments/count-by-status?${queryString}`
          : `/v5/shipments/count-by-status`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  getShipmentDetails = async (shipmentId: number): Promise<APIResponse> => {
    try {
      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}`,
      );
      return response;
    } catch (error) {
      throw error;
    }
  };

  // ── Admin: Shipment inventory lines ───────────────────────────────────────

  addShipmentPackLine = async (
    shipmentId: number,
    payload: AddShipmentPackLineRequest,
  ): Promise<AddShipmentPackLineResult> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/lines/pack`,
        payload,
      );
      return response.result as AddShipmentPackLineResult;
    } catch (error) {
      throw error;
    }
  };

  addShipmentItemLine = async (
    shipmentId: number,
    payload: AddShipmentItemLineRequest,
  ): Promise<AddShipmentItemLineResult> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/lines/item`,
        payload,
      );
      return response.result as AddShipmentItemLineResult;
    } catch (error) {
      throw error;
    }
  };

  bulkAddShipmentPackLine = async (
    payload: BulkAddShipmentPackLineRequest,
  ): Promise<BulkAddShipmentPackLineResult> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/lines/pack/bulk`,
        payload,
      );
      return response.result as BulkAddShipmentPackLineResult;
    } catch (error) {
      throw error;
    }
  };

  bulkAddShipmentItemLine = async (
    payload: BulkAddShipmentItemLineRequest,
  ): Promise<BulkAddShipmentItemLineResult> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/lines/item/bulk`,
        payload,
      );
      return response.result as BulkAddShipmentItemLineResult;
    } catch (error) {
      throw error;
    }
  };

  uploadShipmentLinesCsv = async (
    lineType: ShipmentLineType,
    file: File,
  ): Promise<APIResponse & { result?: ShipmentLinesUploadResult }> => {
    try {
      const response = await uploadCSVAPI(
        `${SHIPMENTS_ADMIN_BASE}/lines/${lineType === "PACK" ? "pack" : "item"}/bulk-upload`,
        file,
      );
      return response as APIResponse & { result?: ShipmentLinesUploadResult };
    } catch (error) {
      throw error;
    }
  };

  getShipmentLinesUploadJob = async (
    jobId: number,
  ): Promise<APIResponse & { result?: ShipmentLinesUploadJobStatus }> => {
    try {
      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/lines/bulk-upload/jobs/${jobId}`,
      );
      return response as APIResponse & {
        result?: ShipmentLinesUploadJobStatus;
      };
    } catch (error) {
      throw error;
    }
  };

  listShipmentLines = async (
    shipmentId: number,
    params?: ListShipmentLinesParams,
  ): Promise<ListShipmentLinesResult> => {
    try {
      const query: Record<string, boolean> = {};
      if (params?.includeRemoved != null) {
        query.includeRemoved = params.includeRemoved;
      }
      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/lines`,
        Object.keys(query).length > 0 ? query : undefined,
      );
      return response.result as ListShipmentLinesResult;
    } catch (error) {
      throw error;
    }
  };

  removeShipmentLine = async (
    shipmentId: number,
    lineId: number,
  ): Promise<RemoveShipmentLineResult> => {
    try {
      const response = await deleteAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/lines/${lineId}`,
      );
      return response.result as RemoveShipmentLineResult;
    } catch (error) {
      throw error;
    }
  };

  removeShipmentPackLines = async (
    shipmentId: number,
    packId: number,
  ): Promise<RemoveShipmentPackLinesResult> => {
    try {
      const response = await deleteAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/lines/pack/${packId}`,
      );
      return response.result as RemoveShipmentPackLinesResult;
    } catch (error) {
      throw error;
    }
  };

  dispatchShipmentLines = async (
    shipmentId: number,
    payload?: DispatchShipmentLinesRequest,
  ): Promise<DispatchShipmentLinesResult> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/dispatch`,
        payload ?? {},
      );
      return response.result as DispatchShipmentLinesResult;
    } catch (error) {
      throw error;
    }
  };

  // ── Admin: Shipment boxes ─────────────────────────────────────────────────

  addShipmentBox = async (
    shipmentId: number,
    payload: AddShipmentBoxRequest,
  ): Promise<AddShipmentBoxResult> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/boxes`,
        payload,
      );
      return unwrapShipmentBoxWriteResult<AddShipmentBoxResult>(
        response,
        "Failed to add shipment box.",
      );
    } catch (error) {
      throw error;
    }
  };

  deleteShipmentBox = async (
    shipmentId: number,
    boxId: number,
  ): Promise<DeleteShipmentBoxResult> => {
    try {
      const response = await deleteAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/boxes/${boxId}`,
      );
      return unwrapShipmentBoxWriteResult<DeleteShipmentBoxResult>(
        response,
        "Failed to remove shipment box.",
      );
    } catch (error) {
      throw error;
    }
  };

  updateShipmentRecipient = async (
    shipmentId: number,
    payload: UpdateShipmentRecipientRequest,
  ): Promise<UpdateShipmentRecipientResult> => {
    try {
      const response = await putAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/recipient`,
        payload,
      );
      return unwrapShipmentSectionResult<UpdateShipmentRecipientResult>(
        response,
        "Failed to update recipient.",
      );
    } catch (error) {
      throw error;
    }
  };

  updateShipmentAddress = async (
    shipmentId: number,
    payload: UpdateShipmentAddressRequest,
  ): Promise<UpdateShipmentAddressResult> => {
    try {
      const response = await putAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/address`,
        payload,
      );
      return unwrapShipmentSectionResult<UpdateShipmentAddressResult>(
        response,
        "Failed to update shipping address.",
      );
    } catch (error) {
      throw error;
    }
  };

  updateShipmentDelivery = async (
    shipmentId: number,
    payload: UpdateShipmentDeliveryRequest,
  ): Promise<UpdateShipmentDeliveryResult> => {
    try {
      const response = await putAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/delivery`,
        payload,
      );
      return unwrapShipmentSectionResult<UpdateShipmentDeliveryResult>(
        response,
        "Failed to update delivery and tracking.",
      );
    } catch (error) {
      throw error;
    }
  };

  /**
   * Paginated structured activity log for one shipment
   * (`GET /v1/shipments/admin/shipment_id/:id/activity-logs`).
   */
  bulkRemovePacksFilters = (
    params: GetShipmentsParams,
  ): Record<string, string> => {
    const query = this.getQueryParams(params);
    query.delete("page_no");
    query.delete("limit");
    query.delete("sort_by");
    query.delete("sort_type");
    query.delete("all_selected");
    const filters: Record<string, string> = {};
    query.forEach((value, key) => {
      if (value !== "") filters[key] = value;
    });
    return filters;
  };

  createBulkRemovePacksJob = async (input: {
    scope: "selected" | "filters";
    shipmentIds: number[];
    filters?: Record<string, string>;
  }): Promise<CreateBulkRemovePacksResult> => {
    try {
      const response = await postAPI(
        `${SHIPMENTS_ADMIN_BASE}/bulk-remove-packs`,
        {
          scope: input.scope,
          shipment_ids:
            input.scope === "selected" ? input.shipmentIds : undefined,
          filters: input.scope === "filters" ? input.filters : undefined,
        },
      );
      if (response.success === false) {
        throw new Error(
          response.message || "Couldn't start bulk remove packs.",
        );
      }
      const job = mapBulkRemovePacksJob((response.result as any)?.job);
      if (!job) {
        throw new Error(
          response.message || "Couldn't start bulk remove packs.",
        );
      }
      return { conflict: false, job };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const jobId = Number(error.response.data?.result?.job_id);
        if (Number.isFinite(jobId) && jobId > 0) {
          return { conflict: true, jobId };
        }
      }
      throw new Error(
        bulkRemovePacksErrorMessage(
          error,
          "Couldn't start bulk remove packs.",
        ),
      );
    }
  };

  getActiveBulkRemovePacksJob = async (): Promise<BulkRemovePacksJob | null> => {
    try {
      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/bulk-remove-packs/active`,
      );
      if (response.success === false) {
        throw new Error(
          response.message || "Couldn't check bulk remove packs.",
        );
      }
      return mapBulkRemovePacksJob((response.result as any)?.job);
    } catch (error) {
      throw new Error(
        bulkRemovePacksErrorMessage(
          error,
          "Couldn't check bulk remove packs.",
        ),
      );
    }
  };

  getBulkRemovePacksJob = async (
    jobId: number,
  ): Promise<BulkRemovePacksJob> => {
    try {
      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/bulk-remove-packs/${jobId}`,
      );
      if (response.success === false) {
        throw new Error(
          response.message || "Couldn't load bulk remove packs.",
        );
      }
      const job = mapBulkRemovePacksJob((response.result as any)?.job);
      if (!job) {
        throw new Error(
          response.message || "Couldn't load bulk remove packs.",
        );
      }
      return job;
    } catch (error) {
      throw new Error(
        bulkRemovePacksErrorMessage(
          error,
          "Couldn't load bulk remove packs.",
        ),
      );
    }
  };

  getShipmentActivityLogs = async (
    shipmentId: number,
    filters: ShipmentActivityLogFilters = {},
  ): Promise<PaginatedAPIResponse<ShipmentActivityLog>> => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("page_no", String(filters.page_no ?? 1));
      queryParams.append("limit", String(filters.limit ?? 20));
      const response = await getAPI(
        `${SHIPMENTS_ADMIN_BASE}/shipment_id/${shipmentId}/activity-logs?${queryParams.toString()}`,
      );
      if (!response?.result) {
        throw new Error(
          response?.message ||
            "Failed to fetch activity logs. Please try again later.",
        );
      }
      return response.result;
    } catch (error) {
      throw error;
    }
  };
}
